import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Mail, Phone, Send } from "lucide-react";
import { useSite, UI, API, BACKEND_URL } from "@/lib/site";
import { MaskLine, Reveal } from "@/components/Reveal";
import { SocialButtons } from "@/components/WaButton";
import { Ornament } from "@/components/Logo";

const field = "w-full rounded-none border-0 border-b border-[#e6ca7a]/30 bg-transparent px-0 py-4 text-base text-[#f6ecd9] placeholder:text-[#a89c87] focus:border-[#e6ca7a] focus:outline-none focus:ring-0 transition-colors duration-300";

export const Contact = ({ defaultDirection = "" }) => {
  const { t, lang, content } = useSite();
  const c = content?.contacts || {};
  const [form, setForm] = useState({ name: "", phone: "", direction: defaultDirection, message: "", website: "" });
  const startedAt = useRef(Date.now());
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setLoading(true);
    try {
      if (!BACKEND_URL) throw new Error("no backend");
      await axios.post(`${API}/leads`, { ...form, lang, elapsed_ms: Date.now() - startedAt.current });
      toast.success(t(UI.formSent));
      setForm({ name: "", phone: "", direction: defaultDirection, message: "", website: "" });
      startedAt.current = Date.now();
    } catch (err) {
      toast.error(err?.response?.status === 429 ? t(UI.formLimit) : t(UI.formErr));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacts" data-testid="contact-section" className="grain relative overflow-hidden bg-[#0e0c0a] px-5 py-24 text-[#f6ecd9] md:px-10 md:py-36">
      <img src="/media/silk-dark.jpg" alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-[1400px] gap-16 md:grid-cols-12">
        <div className="md:col-span-6">
          <p className="eyebrow !text-[#e6ca7a]">{t(UI.nav.contacts)}</p>
          <h2 className="font-display mt-6 text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl">
            <MaskLine inView>{t(UI.contactTitle)}</MaskLine>
          </h2>
          <Ornament className="mt-8 h-4 w-48" color="#e6ca7a" />
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#d8ccb6]">{t(UI.contactLead)}</p>
          <div className="mt-10 space-y-4 text-[0.95rem]">
            <p className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#e6ca7a]" /><span data-testid="contact-phone">{c.phone}</span></p>
            <a href={`mailto:${c.email}`} className="flex items-center gap-3 hover:text-[#e6ca7a]" data-testid="contact-email"><Mail className="h-4 w-4 text-[#e6ca7a]" />{c.email}</a>
            <a href={`https://instagram.com/${c.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#e6ca7a]" data-testid="contact-instagram"><Instagram className="h-4 w-4 text-[#e6ca7a]" />@{c.instagram}</a>
          </div>
          <SocialButtons label={t(UI.writeWa)} className="mt-10" testPrefix="contact" dark />
        </div>
        <Reveal className="md:col-span-6">
          <form onSubmit={submit} className="rounded-[26px] border border-[#e6ca7a]/20 bg-[#171310]/70 p-7 backdrop-blur-xl md:p-10" data-testid="lead-form">
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
            <input className={field} placeholder={t(UI.formName)} value={form.name} onChange={set("name")} required maxLength={120} data-testid="lead-name-input" />
            <input className={field} placeholder={t(UI.formPhone)} value={form.phone} onChange={set("phone")} required maxLength={60} data-testid="lead-phone-input" />
            <select className={`${field} appearance-none`} value={form.direction} onChange={set("direction")} data-testid="lead-direction-select">
              <option value="" className="bg-[#171310]">{t(UI.formDir)}: {t(UI.formAny)}</option>
              {(content?.directions || []).map((d) => (
                <option key={d.slug} value={d.slug} className="bg-[#171310]">{t(d.title)}</option>
              ))}
            </select>
            <textarea className={`${field} min-h-[110px] resize-none`} placeholder={t(UI.formMsg)} value={form.message} onChange={set("message")} maxLength={2000} data-testid="lead-message-input" />
            <button type="submit" disabled={loading} className="btn-gold mt-8 w-full justify-center disabled:opacity-60" data-testid="lead-submit-btn">
              <Send className="h-4 w-4" /> {loading ? "…" : t(UI.formSend)}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
